package data.network

import domain.repository.AuthenticationRepository
import data.storage.SecureStorage
import data.storage.StorageKeys
import data.datasource.remote.PlatformApi
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.cookies.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json

/**
 * Authenticated HTTP client that automatically handles token invalidation
 */
class AuthenticatedHttpClient(
    private val authRepository: AuthenticationRepository
) {
    
    val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                coerceInputValues = true
            })
        }
        install(HttpCookies)
        
        // Install response validation to catch authentication errors
        HttpResponseValidator {
            validateResponse { response ->
                when (response.status) {
                    HttpStatusCode.Unauthorized -> {
                        println("🚫 HTTP Client: Received 401 Unauthorized, token is invalid")
                        // Launch coroutine to handle logout
                        GlobalScope.launch {
                            authRepository.handleInvalidToken()
                        }
                    }
                    HttpStatusCode.Forbidden -> {
                        println("🚫 HTTP Client: Received 403 Forbidden, checking if token-related")
                        // Check if it's a token issue based on response content or headers
                        val responseText = try {
                            response.bodyAsText()
                        } catch (e: Exception) {
                            ""
                        }
                        if (responseText.contains("token", ignoreCase = true) || 
                            responseText.contains("unauthorized", ignoreCase = true)) {
                            println("🚫 HTTP Client: 403 appears to be token-related, logging out")
                            GlobalScope.launch {
                                authRepository.handleInvalidToken()
                            }
                        }
                    }
                }
            }
        }
    }
    
    /**
     * Make an authenticated request with automatic cookie handling
     * Uses cookies for authentication (no Bearer token needed)
     */
    suspend fun authenticatedRequest(
        block: suspend HttpRequestBuilder.() -> Unit
    ): HttpResponse {
        // The HttpClient will automatically send cookies
        // Our Vercel API endpoints read the github_token cookie for auth
        return client.request {
            headers {
                append("Accept", "application/json")
                append("Content-Type", "application/json")
                append("User-Agent", "PR-Dashboard-App")
            }
            block()
        }
    }
    
    fun close() {
        client.close()
    }
}
